import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Switch } from '@/components/ui/switch';
import { BellRing, FileText, MessageSquare, Briefcase, ShieldCheck, FileSpreadsheet, Ticket } from 'lucide-react';
import { toast } from 'sonner';

const ALERT_TYPES = [
  {
    key: 'notify_new_price_request',
    icon: FileText,
    title: 'Nouvelle demande de prix',
    titleEn: 'New RFQ available',
    desc: 'Recevez un email à chaque nouvelle demande de prix publiée par ProdLink.',
    descEn: 'Get an email each time ProdLink publishes a new RFQ.',
  },
  {
    key: 'notify_new_message',
    icon: MessageSquare,
    title: 'Nouveau message',
    titleEn: 'New message',
    desc: 'Alerté quand ProdLink vous envoie un message dans une conversation.',
    descEn: 'Get notified when ProdLink sends you a message in a conversation.',
  },
  {
    key: 'notify_project_assigned',
    icon: Briefcase,
    title: 'Projet attribué',
    titleEn: 'Project assigned',
    desc: 'Alerté quand un nouveau projet vous est confié.',
    descEn: 'Get notified when a new project is assigned to you.',
  },
  {
    key: 'notify_nda_pending',
    icon: ShieldCheck,
    title: 'NDA à signer',
    titleEn: 'NDA to sign',
    desc: 'Alerté quand un accord de confidentialité attend votre signature.',
    descEn: 'Get notified when a non-disclosure agreement is waiting for your signature.',
  },
  {
    key: 'notify_quote_status',
    icon: FileSpreadsheet,
    title: 'Statut de votre devis',
    titleEn: 'Quote status update',
    desc: 'Alerté quand un de vos devis est accepté ou refusé par le client final.',
    descEn: 'Get notified when one of your quotes is accepted or rejected by the end customer.',
  },
  {
    key: 'notify_ticket_resolved',
    icon: Ticket,
    title: 'Ticket résolu',
    titleEn: 'Ticket resolved',
    desc: 'Alerté quand un de vos tickets est marqué comme résolu.',
    descEn: 'Get notified when one of your tickets is marked as resolved.',
  },
];

export default function SubcontractorNotificationsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/subcontractors/me/notification-settings');
        setSettings(data);
      } catch (e) {
        toast.error('Failed to load notification settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = async (key, value) => {
    setSavingKey(key);
    const previous = settings[key];
    setSettings(s => ({ ...s, [key]: value }));  // optimistic
    try {
      const { data } = await api.put('/subcontractors/me/notification-settings', { [key]: value });
      setSettings(data);
      toast.success(value ? 'Alert enabled' : 'Alert disabled');
    } catch (e) {
      setSettings(s => ({ ...s, [key]: previous }));  // rollback
      toast.error('Could not save');
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return <div className="text-zinc-500 py-10 text-center">Loading…</div>;
  }
  if (!settings) {
    return <div className="text-rose-400 py-10 text-center">Could not load notification settings.</div>;
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="subcontractor-notifications-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <BellRing className="w-7 h-7 text-amber-400" />
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-zinc-100" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Notifications
          </h1>
          <p className="text-xs sm:text-sm text-zinc-500 mt-0.5">
            Manage which events trigger an email alert. <span className="text-zinc-600">· Gérez les événements qui vous envoient un email.</span>
          </p>
        </div>
      </div>

      {/* List of toggles */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm divide-y divide-zinc-800">
        {ALERT_TYPES.map(({ key, icon: Icon, title, titleEn, desc, descEn }) => (
          <div
            key={key}
            className="flex items-start sm:items-center gap-4 p-4 sm:p-5"
            data-testid={`alert-row-${key}`}
          >
            <div className="w-9 h-9 rounded-sm bg-amber-500/10 flex items-center justify-center shrink-0">
              <Icon className="w-4 h-4 text-amber-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-zinc-100">
                {title} <span className="text-zinc-500 font-normal">· {titleEn}</span>
              </div>
              <p className="text-[12px] text-zinc-500 mt-0.5">
                {desc}
                <br />
                <span className="text-zinc-600">{descEn}</span>
              </p>
            </div>
            <Switch
              checked={!!settings[key]}
              onCheckedChange={(v) => toggle(key, v)}
              disabled={savingKey === key}
              data-testid={`toggle-${key}`}
              className="data-[state=checked]:bg-amber-500 shrink-0"
              aria-label={title}
            />
          </div>
        ))}
      </div>

      {/* Footer note */}
      <div className="text-[11px] text-zinc-600 italic px-1">
        Each email contains a one-click link that brings you directly back to ProdPilot.
        <br />
        Chaque email contient un lien direct qui vous ramène sur ProdPilot.
      </div>
    </div>
  );
}
